import type { AnalyticsResponse } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { formatCurrencyCompact } from "@/lib/format";
import { Tabs } from "@/components/ui/Tabs";
import { ANALYTICS_DETAIL_TABS, type AnalyticsDetailTab } from "./params";

/**
 * The strip's **Detailed view** — the panel that replaced the standalone Analytics screen.
 *
 * Two tabs, switchable rather than stacked, over the *same* response the tiles above are
 * rendered from: the pipeline broken out **by phase** and **by manager**. It lives under the
 * strip on the pipeline route, which is where the numbers are used, and its expansion and
 * active tab live in the URL — so an expanded, filtered view is still a link somebody can
 * paste into Slack, which is the one property the removed screen had that mattered.
 *
 * Both tabs are bars, and every bar clicks through. A figure a reader cannot open is a figure
 * they cannot act on — the book's rule, and the reason neither tab is a chart.
 */
export function DetailedView({
  data,
  tab,
  onTabChange,
}: {
  data: AnalyticsResponse;
  tab: AnalyticsDetailTab;
  onTabChange: (tab: AnalyticsDetailTab) => void;
  /** The current URL's query string, so a bar's link keeps the filters already applied. */
}): React.JSX.Element {
  const t = useT();

  return (
    <div className="bg-card rounded-md border p-4">
      <Tabs
        label={t("analytics.detailedView")}
        idPrefix="analytics"
        tabs={ANALYTICS_DETAIL_TABS.map((value) => ({
          value,
          label: t(value === "phase" ? "analytics.tabPhase" : "analytics.tabManager"),
        }))}
        active={tab}
        onChange={onTabChange}
      >
        {tab === "phase" ? <ByPhase data={data} /> : <ByManager data={data} />}
      </Tabs>
    </div>
  );
}

/**
 * Pipeline distribution: horizontal bars in stage order, scaled by **value** with the count
 * printed on each (Decision Log rows 25 and 50). Unchanged by the redesign — it only moved
 * house, from a screen into this panel.
 *
 * Explicitly not a funnel. A funnel asserts conversion rates version 1.0 does not calculate,
 * and stage order is the pipeline's own sequence — a large value sitting in an early stage is
 * a different business situation from the same value near closure, which is the whole point
 * of showing distribution rather than a total.
 */
function ByPhase({ data }: { data: AnalyticsResponse }) {
  const t = useT();
  // The scale is the largest bar, so the shape is readable at any absolute size. Not a sum:
  // bars scaled to the total are all short whenever the pipeline is spread evenly.
  const peak = Math.max(...data.distribution.map((row) => Number(row.totalNip)), 0);

  // No heading: the tab above already says which aggregate this is, and repeating it in an
  // <h2> underneath was a second label for one thing. The section keeps an accessible name
  // from the tab that controls it.
  return (
    <section aria-label={t("analytics.tabPhase")}>
      <ul className="space-y-2">
        {data.distribution.map((row) => {
          const value = Number(row.totalNip);
          const width = peak > 0 ? Math.max((value / peak) * 100, value > 0 ? 2 : 0) : 0;

          return (
            <li key={row.stageId}>
              {/* Read-only (2026-08-25): these lines are an aggregate to look at, not a
                  control. They used to filter the board, which made a glance at the numbers
                  one mis-click away from rearranging the screen the reader was reasoning
                  about. The filter panel is where filtering belongs. */}
              <div className="block rounded-md">
                <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{row.stageName}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {formatCurrencyCompact(row.totalNip)}
                    {/* The count alongside the value: money is what a review weighs, and the
                        count is what catches many-small-deals. */}
                    <span className="ml-2">{t("count.deals", row.count)}</span>
                  </span>
                </div>
                <div className="bg-muted h-2.5 w-full overflow-hidden rounded-full">
                  <div className="bg-primary/70 h-full rounded-full" style={{ width: `${width}%` }} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Pipeline by manager — **for every reader**, not the Head of Sales alone.
 *
 * It is the shared board's own data sliced by owner: every manager already sees every card
 * and the owner on it, so role-gating this bought a secrecy nobody actually had. What it
 * adds over the cards is the arithmetic, and the arithmetic is the whole reason to look.
 *
 * Three things are load-bearing:
 *
 * - **The bar wears the owner's `--manager-*` colour**, from the one ownership-colour system
 *   the cards and the drop previews use. An owner with no colour assigned gets the neutral
 *   treatment, exactly as their cards do — never a fallback hue that would read as an
 *   assignment nobody made.
 * - **The stalled share is a segment inside the bar, distinguished by texture** rather than by
 *   a second colour, because the bar's colour is already spoken for by ownership — and because
 *   colour is never the sole carrier of status here.
 * - **Both figures are printed** to the right of the bar: the total, and the stalled amount
 *   inside it. That is what keeps the segment honest for a reader who cannot see it at all.
 */
function ByManager({ data }: { data: AnalyticsResponse }) {
  const t = useT();
  const peak = Math.max(...data.byManager.map((row) => Number(row.totalNip)), 0);

  if (data.byManager.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("analytics.noManagers")}</p>;
  }

  return (
    <section aria-label={t("analytics.tabManager")}>
      <ul className="space-y-2">
        {data.byManager.map((row) => {
          const total = Number(row.totalNip);
          const stalled = Number(row.stalledNip);
          const width = peak > 0 ? Math.max((total / peak) * 100, total > 0 ? 2 : 0) : 0;
          // The stalled share is measured *inside this manager's own bar*, not against the
          // peak — it is the part of their money that is stuck, which is a proportion of
          // theirs and of nobody else's.
          const stalledShare = total > 0 ? Math.min((stalled / total) * 100, 100) : 0;

          return (
            <li key={row.ownerId}>
              {/* Read-only, like the phase bars — an aggregate to read, not a control. */}
              <div className="block rounded-md">
                <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{row.ownerName}</span>
                  {/* The value column: total, then the stalled amount inside it. */}
                  <span className="text-muted-foreground shrink-0 tabular-nums">
                    <span className="text-foreground font-medium">{formatCurrencyCompact(row.totalNip)}</span>
                    <span className="ml-2">{t("analytics.stalledAmount", formatCurrencyCompact(row.stalledNip))}</span>
                    <span className="ml-2">{t("count.deals", row.count)}</span>
                  </span>
                </div>
                <div className="bg-muted h-2.5 w-full overflow-hidden rounded-full">
                  <div
                    // One colour for every bar (2026-08-25). The owner's own hue was tried
                    // and dropped: a chart where each bar is a different colour reads as a
                    // categorical claim, and comparing lengths is the only thing this chart
                    // is for. Ownership is named on the line beside it.
                    //
                    // `overflow-hidden` is load-bearing, not tidiness: the stalled segment
                    // below is a square span, and without clipping it paints over its parent's
                    // rounded right edge — the cap turns square exactly when a manager's whole
                    // pipeline is stalled.
                    className="bg-primary/70 flex h-full justify-end overflow-hidden rounded-full"
                    style={{ width: `${width}%` }}
                  >
                    {stalledShare > 0 && (
                      <span
                        className="stalled-segment h-full"
                        style={{
                          width: `${stalledShare}%`,
                          // The separating edge, in the panel's own surface colour, so the
                          // segment reads as a division of the bar rather than a smudge on it.
                          borderLeft: stalledShare < 100 ? "2px solid var(--card)" : undefined,
                        }}
                        aria-hidden
                      />
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
